import { useState, useEffect, useMemo, Fragment } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import {
  MapPinIcon,
  PlusIcon,
  ArrowPathIcon,
  TrashIcon,
  PencilIcon,
  ArrowLeftIcon,
  MapIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ChevronDownIcon,
  CheckIcon,
  ArrowUpTrayIcon,
  BuildingStorefrontIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { craApi, fairLendingApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import { usePrefetchMapLibrary } from '../hooks/usePrefetchMapLibrary';
import PlaceOnMapModal from '../components/branches/PlaceOnMapModal';

const AssessmentAreaMap = lazy(() => import('../components/cra/AssessmentAreaMap'));
const AssessmentAreaSmallBusinessDensity = lazy(() => import('../components/cra/AssessmentAreaSmallBusinessDensity'));
const AssessmentAreaCsvImportModal = lazy(() => import('../components/cra/AssessmentAreaCsvImportModal'));

export default function CraAssessmentAreasPage() {
  const { teamId, isLoading: teamLoading } = useTeamId();
  usePrefetchMapLibrary();
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [placing, setPlacing] = useState<{ branchId: string; branchName: string; address: string } | null>(null);
  const [editingArea, setEditingArea] = useState<any>(null);
  const [mapAreaId, setMapAreaId] = useState<string | null>(null);
  const [densityAreaId, setDensityAreaId] = useState<string | null>(null);
  const [analysisPanel, setAnalysisPanel] = useState<{ areaId: string; type: 'excluded' | 'concentration' } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['cra-assessment-areas', teamId],
    queryFn: async () => {
      const res = await craApi.listAssessmentAreas(teamId!);
      return res.data;
    },
    enabled: !!teamId,
  });

  // Branch AA validation — explicit teamId because the /fair-lending/*
  // namespace doesn't take teamId in the URL and the resolveTeam middleware
  // can't auto-resolve for users with multiple teams (it would leave teamId
  // undefined, which Prisma silently drops, returning branches across all
  // the user's teams — a cross-tenant data leak).
  const { data: branchValidation } = useQuery({
    queryKey: ['branch-aa-validation', teamId],
    queryFn: async () => {
      const res = await fairLendingApi.validateBranchesAgainstAA(teamId!);
      return res.data;
    },
    enabled: !!teamId,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => craApi.deleteAssessmentArea(teamId!, id),
    onSuccess: () => {
      toast.success('Assessment area deleted');
      queryClient.invalidateQueries({ queryKey: ['cra-assessment-areas'] });
    },
    onError: () => toast.error('Failed to delete assessment area'),
  });

  const refreshMutation = useMutation({
    mutationFn: (id: string) => craApi.refreshAssessmentArea(teamId!, id),
    onSuccess: () => {
      toast.success('Demographics refreshed');
      queryClient.invalidateQueries({ queryKey: ['cra-assessment-areas'] });
    },
    onError: () => toast.error('Failed to refresh demographics'),
  });

  if (teamLoading || isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-64 bg-gray-200 rounded" />
        <div className="h-96 bg-gray-200 rounded-lg" />
      </div>
    );
  }

  const areas = data?.assessmentAreas || [];
  const validationResults = branchValidation?.validationResults || [];
  const errors = validationResults.filter((r: any) => r.severity === 'ERROR');
  const warnings = validationResults.filter((r: any) => r.severity === 'WARNING');

  return (
    <div>
      <div className="mb-6">
        <Link to="/cra" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-3">
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to CRA Dashboard
        </Link>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-navy-900">Assessment Areas</h1>
            <p className="mt-1 text-sm text-gray-500">Define your institution's geographic CRA assessment areas</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCsvImportOpen(true)}
              className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium border border-ocean-600 text-ocean-700 bg-white hover:bg-ocean-50 transition-colors"
              title="Bulk import assessment areas from a CSV file"
            >
              <ArrowUpTrayIcon className="h-4 w-4" />
              Import CSV
            </button>
            {areas.length > 0 && (
              <button
                onClick={() => { setEditingArea(null); setModalOpen(true); }}
                className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium bg-ocean-600 text-white hover:bg-ocean-700"
              >
                <PlusIcon className="h-4 w-4" />
                Add Area
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Branch Validation Warnings */}
      {validationResults.length > 0 && (
        <div className={`rounded-lg border p-4 mb-6 ${errors.length > 0 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${errors.length > 0 ? 'text-red-500' : 'text-amber-500'}`} />
            <div>
              <h3 className={`text-sm font-semibold ${errors.length > 0 ? 'text-red-800' : 'text-amber-800'}`}>
                Branch Location Issues
              </h3>
              <ul className="mt-1 text-sm space-y-1">
                {errors.map((r: any) => (
                  <li key={r.branchId} className="text-red-700">
                    <strong>{r.branchName}</strong> — {r.message}
                    {r.reason === 'LOCATION_UNRESOLVED' && (
                      <button
                        onClick={() => setPlacing({ branchId: r.branchId, branchName: r.branchName, address: r.address })}
                        className="ml-2 font-medium text-ocean-700 underline hover:text-ocean-800"
                      >
                        Place on map
                      </button>
                    )}
                  </li>
                ))}
                {warnings.map((r: any) => (
                  <li key={r.branchId} className="text-amber-700">
                    <strong>{r.branchName}</strong> — {r.message}
                    {r.reason === 'LOCATION_UNRESOLVED' && (
                      <button
                        onClick={() => setPlacing({ branchId: r.branchId, branchName: r.branchName, address: r.address })}
                        className="ml-2 font-medium text-ocean-700 underline hover:text-ocean-800"
                      >
                        Place on map
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {placing && (
        <PlaceOnMapModal
          branchId={placing.branchId}
          branchName={placing.branchName}
          addressLine={placing.address}
          onClose={() => setPlacing(null)}
        />
      )}

      {/* Summary Cards */}
      {areas.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-5">
            <p className="text-sm text-gray-500">Assessment Areas</p>
            <p className="text-2xl font-semibold text-navy-900 tabular-nums">{areas.length}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-5">
            <p className="text-sm text-gray-500">Total Census Tracts</p>
            <p className="text-2xl font-semibold text-navy-900 tabular-nums">
              {areas.reduce((sum: number, a: any) => sum + (a.totalTracts || 0), 0).toLocaleString()}
            </p>
          </div>
          <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-5">
            <p className="text-sm text-gray-500">LMI Tracts</p>
            <p className="text-2xl font-semibold text-navy-900 tabular-nums">
              {areas.reduce((sum: number, a: any) => sum + (a.lmiTracts || 0), 0).toLocaleString()}
            </p>
          </div>
          <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-5">
            <p className="text-sm text-gray-500">Majority-Minority Tracts</p>
            <p className="text-2xl font-semibold text-navy-900 tabular-nums">
              {areas.reduce((sum: number, a: any) => sum + (a.majorityMinority || 0), 0).toLocaleString()}
            </p>
          </div>
        </div>
      )}

      {/* Areas Table */}
      <div className="bg-white rounded-lg shadow">
        {areas.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Year</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Geography</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Tracts</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">LMI</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Majority-Minority</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {areas.map((area: any) => {
                  const geoLabels: string[] = [];
                  if (area.msaCodes?.length) geoLabels.push(`${area.msaCodes.length} MSA/MD`);
                  if (area.countyCodes?.length) geoLabels.push(`${area.countyCodes.length} counties`);
                  if (area.tractCodes?.length) geoLabels.push(`${area.tractCodes.length} tracts`);

                  return (
                    <tr key={area.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{area.name}</div>
                        {area.description && (
                          <div className="text-xs text-gray-500 mt-0.5">{area.description}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 tabular-nums">{area.referenceYear}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {geoLabels.join(', ') || '—'}
                        {area.totalTracts === 0 && (
                          <span className="ml-2 inline-flex items-center rounded bg-red-50 px-1.5 py-0.5 text-xs text-red-600">
                            No tracts found
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 tabular-nums text-right">{area.totalTracts?.toLocaleString()}</td>
                      <td className="px-6 py-4 text-sm text-right">
                        <span className="text-gray-900 tabular-nums">{area.lmiTracts}</span>
                        {area.totalTracts > 0 && (
                          <span className="text-gray-400 ml-1 text-xs">
                            ({((area.lmiTracts / area.totalTracts) * 100).toFixed(1)}%)
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-right">
                        <span className="text-gray-900 tabular-nums">{area.majorityMinority ?? 0}</span>
                        {area.totalTracts > 0 && (
                          <span className="text-gray-400 ml-1 text-xs">
                            ({(((area.majorityMinority || 0) / area.totalTracts) * 100).toFixed(1)}%)
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* Analysis dropdown */}
                          <AnalysisDropdown
                            onSelect={(type) => setAnalysisPanel(
                              analysisPanel?.areaId === area.id && analysisPanel?.type === type
                                ? null
                                : { areaId: area.id, type }
                            )}
                            activeType={analysisPanel?.areaId === area.id ? analysisPanel!.type : null}
                          />
                          <button
                            onClick={() => setMapAreaId(mapAreaId === area.id ? null : area.id)}
                            className={`p-1.5 rounded ${mapAreaId === area.id ? 'text-ocean-600 bg-ocean-50' : 'text-gray-400 hover:text-ocean-600'}`}
                            title="View map"
                          >
                            <MapIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setDensityAreaId(densityAreaId === area.id ? null : area.id)}
                            className={`p-1.5 rounded ${densityAreaId === area.id ? 'text-ocean-600 bg-ocean-50' : 'text-gray-400 hover:text-ocean-600'}`}
                            title="Small business density (how many SBs, how many in MMCT)"
                          >
                            <BuildingStorefrontIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => refreshMutation.mutate(area.id)}
                            disabled={refreshMutation.isPending}
                            className="p-1.5 text-gray-400 hover:text-ocean-600 rounded"
                            title="Refresh demographics"
                          >
                            <ArrowPathIcon className={`h-4 w-4 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
                          </button>
                          <button
                            onClick={() => { setEditingArea(area); setModalOpen(true); }}
                            className="p-1.5 text-gray-400 hover:text-ocean-600 rounded"
                            title="Edit"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              if (confirm('Delete this assessment area?')) {
                                deleteMutation.mutate(area.id);
                              }
                            }}
                            className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                            title="Delete"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-6 py-16 text-center">
            <MapPinIcon className="mx-auto h-12 w-12 text-gray-300 mb-4" />
            <h3 className="text-base font-semibold text-navy-900">No Assessment Areas</h3>
            <p className="mt-1 text-sm text-gray-500 max-w-sm mx-auto">
              Define your CRA assessment areas by MSA, county, or census tract codes. Demographics will be auto-calculated from FFIEC census data.
            </p>
            <button
              onClick={() => { setEditingArea(null); setModalOpen(true); }}
              className="mt-4 inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium bg-ocean-600 text-white hover:bg-ocean-700"
            >
              <PlusIcon className="h-4 w-4" />
              Add First Area
            </button>
          </div>
        )}
      </div>

      {/* Analysis Panel */}
      {analysisPanel && (
        <AnalysisPanel
          teamId={teamId!}
          areaId={analysisPanel.areaId}
          type={analysisPanel.type}
          areaName={areas.find((a: any) => a.id === analysisPanel.areaId)?.name || ''}
          onClose={() => setAnalysisPanel(null)}
        />
      )}

      {/* Map View */}
      {mapAreaId && (
        <div className="bg-white rounded-lg shadow mt-6">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Assessment Area Map — {areas.find((a: any) => a.id === mapAreaId)?.name || ''}
            </h2>
            <button onClick={() => setMapAreaId(null)} className="text-sm text-gray-500 hover:text-gray-700">
              Close Map
            </button>
          </div>
          <div className="p-6">
            <Suspense fallback={
              <div className="h-[500px] bg-gray-100 rounded-lg flex items-center justify-center">
                <p className="text-sm text-gray-500">Loading map...</p>
              </div>
            }>
              <AssessmentAreaMap
                teamId={teamId!}
                assessmentAreaId={mapAreaId}
                assessmentAreaName={areas.find((a: any) => a.id === mapAreaId)?.name}
              />
            </Suspense>
          </div>
        </div>
      )}

      {/* Small Business Density View */}
      {densityAreaId && (
        <div className="mt-6">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Small Business Density — {areas.find((a: any) => a.id === densityAreaId)?.name || ''}
              </h2>
              <button onClick={() => setDensityAreaId(null)} className="text-sm text-gray-500 hover:text-gray-700">
                Close
              </button>
            </div>
            <div className="p-6">
              <Suspense fallback={
                <div className="h-32 bg-gray-50 rounded-lg flex items-center justify-center">
                  <p className="text-sm text-gray-500">Loading...</p>
                </div>
              }>
                <AssessmentAreaSmallBusinessDensity
                  teamId={teamId!}
                  assessmentAreaId={densityAreaId}
                  assessmentAreaName={areas.find((a: any) => a.id === densityAreaId)?.name}
                />
              </Suspense>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {modalOpen && (
        <AssessmentAreaModal
          teamId={teamId!}
          area={editingArea}
          onClose={() => { setModalOpen(false); setEditingArea(null); }}
        />
      )}

      {/* CSV Import Modal */}
      {csvImportOpen && (
        <Suspense fallback={null}>
          <AssessmentAreaCsvImportModal
            teamId={teamId!}
            isOpen={csvImportOpen}
            onClose={() => setCsvImportOpen(false)}
          />
        </Suspense>
      )}
    </div>
  );
}

// ─── Analysis Dropdown ──────────────────────────────────────────────────────

function AnalysisDropdown({ onSelect, activeType }: {
  onSelect: (type: 'excluded' | 'concentration') => void;
  activeType: 'excluded' | 'concentration' | null;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`p-1.5 rounded flex items-center gap-0.5 ${activeType ? 'text-ocean-600 bg-ocean-50' : 'text-gray-400 hover:text-ocean-600'}`}
        title="Analysis"
      >
        <ChartBarIcon className="h-4 w-4" />
        <ChevronDownIcon className="h-3 w-3" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-20 mt-1 w-56 rounded-md bg-white shadow-lg ring-1 ring-black/5">
            <div className="py-1">
              <button
                onClick={() => { onSelect('excluded'); setOpen(false); }}
                className={`block w-full px-4 py-2 text-left text-sm ${activeType === 'excluded' ? 'bg-ocean-50 text-ocean-700' : 'text-gray-700 hover:bg-gray-50'}`}
              >
                Excluded Geography Analysis
              </button>
              <button
                onClick={() => { onSelect('concentration'); setOpen(false); }}
                className={`block w-full px-4 py-2 text-left text-sm ${activeType === 'concentration' ? 'bg-ocean-50 text-ocean-700' : 'text-gray-700 hover:bg-gray-50'}`}
              >
                Lending Concentration (REMA)
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Analysis Panel ─────────────────────────────────────────────────────────

function AnalysisPanel({ teamId, areaId, type, areaName, onClose }: {
  teamId: string; areaId: string; type: 'excluded' | 'concentration'; areaName: string; onClose: () => void;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['aa-analysis', areaId, type],
    queryFn: async () => {
      if (type === 'excluded') {
        const res = await craApi.getExcludedGeography(teamId, areaId);
        return res.data;
      }
      // For concentration, we'd need a validationId — use latest
      // For now show a placeholder
      return null;
    },
  });

  return (
    <div className="bg-white rounded-lg shadow mt-6">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">
          {type === 'excluded' ? 'Excluded Geography Analysis' : 'Lending Concentration (REMA)'} — {areaName}
        </h2>
        <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700">Close</button>
      </div>
      <div className="p-6">
        {isLoading ? (
          <div className="animate-pulse space-y-3">
            <div className="h-4 w-48 bg-gray-200 rounded" />
            <div className="h-32 bg-gray-100 rounded" />
          </div>
        ) : error ? (
          <p className="text-sm text-red-600">Failed to load analysis. Please try again.</p>
        ) : type === 'excluded' && data ? (
          <ExcludedGeographyView data={data} />
        ) : type === 'concentration' ? (
          <p className="text-sm text-gray-500">
            Lending concentration analysis requires a HMDA validation. Run an FLD analysis first, then return here to view concentrations by county.
          </p>
        ) : (
          <p className="text-sm text-gray-500">No data available.</p>
        )}
      </div>
    </div>
  );
}

function ExcludedGeographyView({ data }: { data: any }) {
  const { included, excluded, riskIndicators, riskLevel } = data;

  const riskColors = { HIGH: 'text-red-600 bg-red-50', MEDIUM: 'text-amber-600 bg-amber-50', LOW: 'text-green-600 bg-green-50' };

  return (
    <div className="space-y-6">
      {/* Risk Level */}
      <div className="flex items-center gap-3">
        <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${riskColors[riskLevel as keyof typeof riskColors] || riskColors.LOW}`}>
          {riskLevel} Risk
        </span>
        {riskIndicators.length > 0 && (
          <span className="text-sm text-gray-500">{riskIndicators.length} concern{riskIndicators.length !== 1 ? 's' : ''} identified</span>
        )}
      </div>

      {/* Risk Indicators */}
      {riskIndicators.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <ul className="space-y-1">
            {riskIndicators.map((ri: string, i: number) => (
              <li key={i} className="text-sm text-amber-800 flex items-start gap-2">
                <ExclamationTriangleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                {ri}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Comparison Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="py-2 pr-4 text-left font-medium text-gray-500">Metric</th>
              <th className="py-2 px-4 text-right font-medium text-gray-500">Included</th>
              <th className="py-2 px-4 text-right font-medium text-gray-500">Excluded</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            <tr><td className="py-2 pr-4 text-gray-700">Total Tracts</td><td className="py-2 px-4 text-right tabular-nums">{included.totalTracts}</td><td className="py-2 px-4 text-right tabular-nums">{excluded.totalTracts}</td></tr>
            <tr><td className="py-2 pr-4 text-gray-700">Low Income</td><td className="py-2 px-4 text-right tabular-nums">{included.low}</td><td className="py-2 px-4 text-right tabular-nums">{excluded.low}</td></tr>
            <tr><td className="py-2 pr-4 text-gray-700">Moderate Income</td><td className="py-2 px-4 text-right tabular-nums">{included.moderate}</td><td className="py-2 px-4 text-right tabular-nums">{excluded.moderate}</td></tr>
            <tr className="font-medium"><td className="py-2 pr-4 text-gray-900">LMI Total</td><td className="py-2 px-4 text-right tabular-nums">{included.lmi} ({included.lmiPct}%)</td><td className="py-2 px-4 text-right tabular-nums">{excluded.lmi} ({excluded.lmiPct}%)</td></tr>
            <tr><td className="py-2 pr-4 text-gray-700">Middle Income</td><td className="py-2 px-4 text-right tabular-nums">{included.middle}</td><td className="py-2 px-4 text-right tabular-nums">{excluded.middle}</td></tr>
            <tr><td className="py-2 pr-4 text-gray-700">Upper Income</td><td className="py-2 px-4 text-right tabular-nums">{included.upper}</td><td className="py-2 px-4 text-right tabular-nums">{excluded.upper}</td></tr>
            <tr className="font-medium"><td className="py-2 pr-4 text-gray-900">Majority-Minority Tracts</td><td className="py-2 px-4 text-right tabular-nums">{included.mmctTracts} ({included.mmctPct}%)</td><td className="py-2 px-4 text-right tabular-nums">{excluded.mmctTracts} ({excluded.mmctPct}%)</td></tr>
            <tr><td className="py-2 pr-4 text-gray-700">Total Population</td><td className="py-2 px-4 text-right tabular-nums">{included.totalPopulation.toLocaleString()}</td><td className="py-2 px-4 text-right tabular-nums">{excluded.totalPopulation.toLocaleString()}</td></tr>
            <tr><td className="py-2 pr-4 text-gray-700">Avg Minority %</td><td className="py-2 px-4 text-right tabular-nums">{included.avgMinorityPct}%</td><td className="py-2 px-4 text-right tabular-nums">{excluded.avgMinorityPct}%</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Create/Edit Modal with MSA Lookup ──────────────────────────────────────

function AssessmentAreaModal({ teamId, area, onClose }: { teamId: string; area?: any; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(area?.name || '');
  const [description, setDescription] = useState(area?.description || '');
  const [referenceYear, setReferenceYear] = useState(area?.referenceYear || 2025);

  // Cascading selections
  const [selectedState, setSelectedState] = useState('');
  const [selectedMsa, setSelectedMsa] = useState('');
  const [selectedCounties, setSelectedCounties] = useState<string[]>(
    area?.countyCodes || [],
  );
  // Names and states for every county the user has picked, accumulated as they browse. The county
  // list only ever holds ONE state's worth, so without this a county chosen under Arkansas becomes
  // an unlabelled FIPS code the moment the user switches to Mississippi.
  const [countyMeta, setCountyMeta] = useState<Record<string, { name: string; stateCode: string }>>({});
  const [msaAllStates, setMsaAllStates] = useState(false);
  const [tractMode, setTractMode] = useState<'all' | 'select'>('all');
  const [selectedTracts, setSelectedTracts] = useState<string[]>(
    area?.tractCodes || [],
  );

  // Cascading data
  const { data: statesData } = useQuery({
    queryKey: ['geo-states', referenceYear],
    queryFn: async () => {
      const res = await craApi.getStates(teamId, referenceYear);
      return res.data;
    },
  });

  const { data: msasData } = useQuery({
    queryKey: ['geo-msas', selectedState, referenceYear],
    queryFn: async () => {
      const res = await craApi.getMsas(teamId, selectedState, referenceYear);
      return res.data;
    },
    enabled: !!selectedState,
  });

  // With an MSA chosen and "all states" on, the state is dropped from the query so a multistate
  // MSA comes back whole rather than one state at a time.
  const countyQueryState = msaAllStates && selectedMsa ? '' : selectedState;
  const { data: countiesData } = useQuery({
    queryKey: ['geo-counties', countyQueryState, selectedMsa, referenceYear],
    queryFn: async () => {
      const res = await craApi.getCounties(teamId, countyQueryState, selectedMsa || undefined, referenceYear);
      return res.data;
    },
    enabled: !!countyQueryState || (msaAllStates && !!selectedMsa),
  });

  // Tracts for all selected counties
  const countyForTracts = selectedCounties.length === 1 ? selectedCounties[0] : '';
  const { data: tractsData } = useQuery({
    queryKey: ['geo-tracts', countyForTracts, referenceYear],
    queryFn: async () => {
      const stCode = countyForTracts.substring(0, 2);
      const coCode = countyForTracts.substring(2, 5);
      const res = await craApi.getTracts(teamId, stCode, coCode, referenceYear);
      return res.data;
    },
    enabled: !!countyForTracts && tractMode === 'select',
  });

  // State and MSA are BROWSING controls — they choose which counties are on screen, not which
  // counties are in the assessment area. That distinction is the whole bug Michael Wallace hit on
  // 2026-09-23 building partial Memphis TN-MS-AR #32820: picking Arkansas and checking Crittenden,
  // then switching to Mississippi for DeSoto, silently discarded Crittenden. "I tried to add just
  // one state/county and then edit/update but that only replaced the first state/county I had
  // added." Editing an existing area was worse — its saved counties loaded, then vanished the
  // instant a state was chosen to add another.
  //
  // So these effects no longer touch `selectedCounties`. The only thing a state change resets is
  // the MSA filter, because the MSA list is fetched per state and a stale code would filter the
  // county list to nothing.
  useEffect(() => {
    setSelectedMsa('');
    setMsaAllStates(false);
  }, [selectedState]);

  const states = statesData?.states || [];
  const msas = msasData?.msas || [];
  // Memoized because the name-backfill effect below depends on it; a fresh [] every render would
  // re-run that effect on every render.
  const counties = useMemo(() => countiesData?.counties || [], [countiesData]);
  const tracts = tractsData?.tracts || [];

  // Toggle county selection
  // Editing an existing area loads its saved county codes with no names attached — the codes were
  // all that was stored. Whenever a county list arrives that contains one of them, its name is
  // adopted, so the chips fill in as the user browses rather than staying as bare FIPS codes.
  useEffect(() => {
    if (!counties.length) return;
    setCountyMeta(prev => {
      let changed = false;
      const next = { ...prev };
      for (const c of counties as any[]) {
        if (selectedCounties.includes(c.code) && !next[c.code]) {
          next[c.code] = {
            name: c.name || `County ${c.countyCode}`,
            stateCode: c.stateCode || c.code.substring(0, 2),
          };
          changed = true;
        }
      }
      return changed ? next : prev; // same reference when nothing changed — no re-render
    });
  }, [counties, selectedCounties]);

  const rememberCounty = (c: any) => {
    setCountyMeta(prev => ({
      ...prev,
      [c.code]: { name: c.name || `County ${c.countyCode}`, stateCode: c.stateCode || c.code.substring(0, 2) },
    }));
  };

  const toggleCounty = (code: string) => {
    const detail = counties.find((c: any) => c.code === code);
    if (detail) rememberCounty(detail);
    setSelectedCounties(prev =>
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code],
    );
    setSelectedTracts([]); // Reset tracts when counties change
  };

  const removeCounty = (code: string) => {
    setSelectedCounties(prev => prev.filter(c => c !== code));
    setSelectedTracts([]);
  };

  // ADDS the visible counties rather than replacing the selection — replacing would wipe whatever
  // was picked under another state, which is the same bug in a different button.
  const selectAllCounties = () => {
    counties.forEach(rememberCounty);
    setSelectedCounties(prev => Array.from(new Set([...prev, ...counties.map((c: any) => c.code)])));
  };
  const clearAllCounties = () => { setSelectedCounties([]); setSelectedTracts([]); };

  /** Everything picked so far, grouped by state, for the running summary. */
  const selectionByState = selectedCounties.reduce<Record<string, string[]>>((acc, code) => {
    const st = countyMeta[code]?.stateCode || code.substring(0, 2);
    (acc[st] ||= []).push(code);
    return acc;
  }, {});
  const stateName = (code: string) =>
    states.find((s: any) => s.code === code)?.name || code;

  // Toggle tract selection
  const toggleTract = (code: string) => {
    setSelectedTracts(prev =>
      prev.includes(code) ? prev.filter(t => t !== code) : [...prev, code],
    );
  };

  const mutation = useMutation({
    mutationFn: (data: any) =>
      area
        ? craApi.updateAssessmentArea(teamId, area.id, data)
        : craApi.createAssessmentArea(teamId, data),
    onSuccess: (res: any) => {
      const totalTracts = res?.data?.totalTracts ?? res?.data?.assessmentArea?.totalTracts;
      if (totalTracts === 0) {
        toast.error('Assessment area saved but no census tracts were found. Check your selections.');
      } else if (totalTracts != null) {
        toast.success(`${area ? 'Updated' : 'Created'} — ${totalTracts.toLocaleString()} census tracts resolved`);
      } else {
        toast.success(area ? 'Assessment area updated' : 'Assessment area created');
      }
      queryClient.invalidateQueries({ queryKey: ['cra-assessment-areas'] });
      onClose();
    },
    onError: () => toast.error(area ? 'Failed to update' : 'Failed to create'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedCounties.length === 0) {
      toast.error('Please select at least one county');
      return;
    }

    mutation.mutate({
      name,
      description: description || undefined,
      msaCodes: selectedMsa ? [selectedMsa] : [],
      countyCodes: selectedCounties,
      tractCodes: tractMode === 'select' ? selectedTracts : [],
      referenceYear,
    });
  };

  const selectClass = 'block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500 bg-white';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            {area ? 'Edit Assessment Area' : 'New Assessment Area'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className={selectClass}
              placeholder="e.g., Memphis AA 2025"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              className={selectClass}
              rows={2}
              placeholder="e.g., Fayette, Shelby, and Tipton Counties"
            />
          </div>

          {/* Reference Year */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reference Year *</label>
            <input
              type="number"
              value={referenceYear}
              onChange={e => setReferenceYear(parseInt(e.target.value) || 2025)}
              className="block w-32 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
              min={2020}
              max={2030}
            />
            <p className="mt-1 text-xs text-gray-500">Year of loan data being evaluated</p>
          </div>

          {/* State — a browse control, not a limit on the area */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Browse by state</label>
            <StateCombobox
              states={states}
              value={selectedState}
              onChange={setSelectedState}
            />
            <p className="mt-1 text-xs text-gray-500">
              Switch states freely — counties you have already checked stay selected. That is how a
              multistate MSA is built.
            </p>
          </div>

          {/* MSA/MD (optional) */}
          {selectedState && msas.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">MSA/MD <span className="text-gray-400 font-normal">(optional)</span></label>
              <select
                value={selectedMsa}
                onChange={e => setSelectedMsa(e.target.value)}
                className={selectClass}
              >
                <option value="">All counties in state (no MSA filter)</option>
                {msas.map((m: any) => (
                  // We hold no MSA/MD titles for FFIEC-ingested data, so the label falls back to
                  // "MSA/MD 32820". Appending the code again would read "MSA/MD 32820 (32820)".
                  <option key={m.code} value={m.code}>
                    {m.name.includes(m.code) ? m.name : `${m.name} (${m.code})`}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Selecting an MSA/MD will filter counties to that metro area.
                {msas.length > 0 && msas.every((m: any) => m.name.includes(m.code)) &&
                  ' Metro area names are not held in the FFIEC census file, so these are listed by code.'}
              </p>
              {selectedMsa && (
                <label className="mt-2 flex items-start gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={msaAllStates}
                    onChange={e => setMsaAllStates(e.target.checked)}
                    className="h-4 w-4 mt-0.5 text-ocean-600 rounded border-gray-300 focus:ring-ocean-500"
                  />
                  <span className="text-gray-700">
                    Show every county in this MSA, across all states
                    <span className="block text-xs text-gray-500">
                      For a multistate MSA such as Memphis TN-MS-AR, this lists the whole metro area
                      at once instead of one state at a time.
                    </span>
                  </span>
                </label>
              )}
            </div>
          )}

          {/* Running selection. Without this a county checked under one state is invisible while
              browsing another, and there is no way to confirm the area is complete before saving. */}
          {selectedCounties.length > 0 && (
            <div className="rounded-md border border-ocean-200 bg-ocean-50 p-3">
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-sm font-medium text-ocean-900">
                  {selectedCounties.length} {selectedCounties.length === 1 ? 'county' : 'counties'} in
                  this assessment area
                  {Object.keys(selectionByState).length > 1
                    ? ' · ' + Object.keys(selectionByState).length + ' states'
                    : ''}
                </span>
                <button
                  type="button"
                  onClick={clearAllCounties}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear all
                </button>
              </div>
              <div className="space-y-1.5">
                {Object.entries(selectionByState).map(([st, codes]) => (
                  <div key={st} className="flex flex-wrap items-baseline gap-1.5">
                    <span className="w-full sm:w-auto sm:mr-1 text-xs font-medium uppercase tracking-wide text-ocean-700">
                      {stateName(st)}
                    </span>
                    {codes.map(code => (
                      <span
                        key={code}
                        className="inline-flex items-center gap-1 rounded-full bg-white border border-ocean-300 px-2 py-0.5 text-xs text-ocean-800"
                      >
                        {countyMeta[code]?.name || code}
                        <button
                          type="button"
                          onClick={() => removeCounty(code)}
                          className="text-ocean-400 hover:text-ocean-700"
                          aria-label={'Remove ' + (countyMeta[code]?.name || code)}
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Counties */}
          {counties.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">Counties *</label>
                <div className="flex gap-2">
                  <button type="button" onClick={selectAllCounties} className="text-xs text-ocean-600 hover:text-ocean-700">
                    Select all shown
                  </button>
                </div>
              </div>
              <div className="border border-gray-300 rounded-md max-h-48 overflow-y-auto">
                {counties.map((c: any) => {
                  const isSelected = selectedCounties.includes(c.code);
                  return (
                    <label
                      key={c.code}
                      className={`flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-0 ${isSelected ? 'bg-ocean-50' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleCounty(c.code)}
                        className="h-4 w-4 text-ocean-600 rounded border-gray-300 focus:ring-ocean-500 mr-3"
                      />
                      <span className={isSelected ? 'font-medium text-ocean-700' : 'text-gray-700'}>
                        {c.name || `County ${c.countyCode}`}
                      </span>
                      <span className="text-gray-400 ml-auto text-xs">({c.countyCode})</span>
                    </label>
                  );
                })}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Showing {counties.length} {counties.length === 1 ? 'county' : 'counties'}
                {msaAllStates && selectedMsa ? ' across every state in this MSA' : ''}. Checking or
                unchecking affects only this list — selections made under other states are kept.
              </p>
            </div>
          )}

          {/* Census Tracts (optional — only when 1 county selected) */}
          {selectedCounties.length === 1 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Census Tracts</label>
              <div className="flex gap-4 mb-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="tractMode"
                    checked={tractMode === 'all'}
                    onChange={() => { setTractMode('all'); setSelectedTracts([]); }}
                    className="h-4 w-4 text-ocean-600 border-gray-300 focus:ring-ocean-500"
                  />
                  All tracts in county
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="tractMode"
                    checked={tractMode === 'select'}
                    onChange={() => setTractMode('select')}
                    className="h-4 w-4 text-ocean-600 border-gray-300 focus:ring-ocean-500"
                  />
                  Select specific tracts
                </label>
              </div>
              {tractMode === 'select' && tracts.length > 0 && (
                <div className="border border-gray-300 rounded-md max-h-48 overflow-y-auto">
                  {tracts.map((t: any) => {
                    const isSelected = selectedTracts.includes(t.code);
                    return (
                      <label
                        key={t.code}
                        className={`flex items-center px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-0 ${isSelected ? 'bg-ocean-50' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleTract(t.code)}
                          className="h-4 w-4 text-ocean-600 rounded border-gray-300 focus:ring-ocean-500 mr-3"
                        />
                        <span className={isSelected ? 'font-medium text-ocean-700' : 'text-gray-700'}>
                          {t.displayCode}
                        </span>
                        {t.incomeLevel && (
                          <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${
                            t.incomeLevel === 'LOW' ? 'bg-red-50 text-red-600' :
                            t.incomeLevel === 'MODERATE' ? 'bg-amber-50 text-amber-600' :
                            t.incomeLevel === 'MIDDLE' ? 'bg-green-50 text-green-600' :
                            'bg-blue-50 text-blue-600'
                          }`}>
                            {t.incomeLevel}
                          </span>
                        )}
                        {t.isMmct && (
                          <span className="ml-1 text-xs px-1.5 py-0.5 rounded bg-purple-50 text-purple-600">MMCT</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
              {tractMode === 'select' && tracts.length > 0 && (
                <p className="mt-1 text-xs text-gray-500">
                  {selectedTracts.length} of {tracts.length} tracts selected
                </p>
              )}
            </div>
          )}

          {selectedCounties.length > 1 && (
            <p className="text-xs text-gray-500 italic">
              Individual tract selection is available when exactly one county is selected. With multiple counties, all tracts are included.
            </p>
          )}

          {/* Submit */}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50">
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending || selectedCounties.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-ocean-600 rounded-md hover:bg-ocean-700 disabled:opacity-50"
            >
              {mutation.isPending ? 'Saving...' : area ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Searchable State Combobox ─────────────────────────────────────────────
// Replaces the native <select> so users can type to filter (e.g., "te" shows
// Tennessee AND Texas instead of jumping straight to the first match).
function StateCombobox({
  states,
  value,
  onChange,
}: {
  states: { code: string; name: string }[];
  value: string;
  onChange: (code: string) => void;
}) {
  const [query, setQuery] = useState('');

  const selected = states.find(s => s.code === value) || null;

  const filtered =
    query === ''
      ? states
      : states.filter(s => {
          const q = query.toLowerCase();
          return s.name.toLowerCase().includes(q) || s.code.toLowerCase().includes(q);
        });

  return (
    <Combobox value={selected} onChange={(s: any) => onChange(s?.code || '')} nullable>
      <div className="relative">
        <div className="relative w-full cursor-default overflow-hidden rounded-md border border-gray-300 bg-white text-left focus-within:border-ocean-500 focus-within:ring-1 focus-within:ring-ocean-500">
          <Combobox.Input
            className="w-full border-none py-2 pl-3 pr-10 text-sm text-gray-900 focus:outline-none focus:ring-0 select-unstyled"
            displayValue={(s: any) => (s ? `${s.name} (${s.code})` : '')}
            onChange={e => setQuery(e.target.value)}
            placeholder="Type to search states..."
          />
          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronDownIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
          </Combobox.Button>
        </div>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => setQuery('')}
        >
          <Combobox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            {filtered.length === 0 && query !== '' ? (
              <div className="relative cursor-default select-none px-4 py-2 text-gray-500">
                No states match "{query}"
              </div>
            ) : (
              filtered.map(s => (
                <Combobox.Option
                  key={s.code}
                  value={s}
                  className={({ active }) =>
                    `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                      active ? 'bg-ocean-50 text-ocean-900' : 'text-gray-900'
                    }`
                  }
                >
                  {({ selected: isSelected, active }) => (
                    <>
                      <span className={`block truncate ${isSelected ? 'font-medium' : 'font-normal'}`}>
                        {s.name} <span className="text-gray-400">({s.code})</span>
                      </span>
                      {isSelected ? (
                        <span
                          className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                            active ? 'text-ocean-700' : 'text-ocean-600'
                          }`}
                        >
                          <CheckIcon className="h-4 w-4" aria-hidden="true" />
                        </span>
                      ) : null}
                    </>
                  )}
                </Combobox.Option>
              ))
            )}
          </Combobox.Options>
        </Transition>
      </div>
    </Combobox>
  );
}
